import type { GeometryCollection, LineString } from "geojson";
import { z } from "zod";

import type {
  ItinerarySegment,
  SurfaceType,
  TrailDifficultyType,
  WaytypeType,
} from "@mountayaapp/types/api/api";
import type {
  SurfaceType as DataSurfaceType,
  TrailDifficultyType as DataTrailDifficultyType,
  WaytypeType as DataWaytypeType,
  ElevationSummary,
  GeometryAnalysis,
  SlopeInfo,
  SurfaceInfo,
  TrailDifficultyInfo,
  WaytypeInfo,
} from "@mountayaapp/types/api/data";

import type { IndexedInfo } from "./elevation";

/**
 * LiveSegmentRouteElevation is the per-route elevation summary the live preview
 * carries: the altitude range along the route (meters above sea level), the
 * total elevation gain/loss (meters), and the steepest ascending/descending
 * gradients (degrees). It mirrors the platform-neutral SegmentRouteElevation
 * from the map package so the projector can be exercised without importing the
 * map leaf (core is a leaf — it cannot depend on map).
 */
export interface LiveSegmentRouteElevation {
  altitudeMax: number;
  altitudeMin: number;
  ascent: number;
  descent: number;
  gradientMax: number;
  gradientMin: number;
}

/**
 * LiveSegmentRouteAnalysis is the surface/way-type/slope/trail-difficulty
 * analysis the live preview carries. Each list classifies a coordinate-index
 * window within the route's geometry; absent when the router did not compute the
 * analysis. The entry shape matches the persisted ItinerarySegment analysis
 * arrays exactly (snake_case indices, REST-vocabulary value) so the projector
 * copies them straight onto the segment without a second translation.
 */
export interface LiveSegmentRouteAnalysis {
  slopeInfo: IndexedInfo<number>[];
  surfaceInfo: IndexedInfo<SurfaceType>[];
  trailDifficultyInfo: IndexedInfo<TrailDifficultyType>[];
  waytypeInfo: IndexedInfo<WaytypeType>[];
}

/**
 * surfaceTypeSchema is the surface-classification vocabulary an analysis band
 * carries — the REST surface_info enum (lower-case, hyphenated) the persisted
 * ItinerarySegment uses, which the wire conversion already normalized the
 * UPPER_CASE Data-API value to. Pinned to the wire SurfaceType via `satisfies` so
 * the accepted set cannot drift from the persisted enum the panel renders.
 */
export const surfaceTypeSchema = z.enum([
  "unknown",
  "paved",
  "unpaved",
  "asphalt",
  "concrete",
  "cobblestone",
  "metal",
  "wood",
  "compacted-gravel",
  "fine-gravel",
  "gravel",
  "dirt",
  "ground",
  "ice",
  "paving-stones",
  "sand",
  "woodchips",
  "grass",
  "grass-paver",
]) satisfies z.ZodType<SurfaceType>;

/**
 * waytypeTypeSchema is the way-type-classification vocabulary an analysis band
 * carries — the REST waytype_info enum the persisted segment uses, normalized
 * from the Data-API value by the wire conversion. Pinned to the wire WaytypeType
 * via `satisfies` so the two cannot drift.
 */
export const waytypeTypeSchema = z.enum([
  "unknown",
  "state-road",
  "road",
  "street",
  "path",
  "track",
  "cycleway",
  "footway",
  "steps",
  "ferry",
  "construction",
]) satisfies z.ZodType<WaytypeType>;

/**
 * trailDifficultyTypeSchema is the trail-difficulty vocabulary an analysis band
 * carries — the REST trail_difficulty_info enum the persisted segment uses,
 * normalized from the Data-API value by the wire conversion. It holds both
 * grading scales: a walking activity is graded on the SAC hiking scale and a
 * cycling one on the mountain-biking scale, and the value names which, so a
 * reader never needs the activity. Pinned to the wire TrailDifficultyType via
 * `satisfies` so the two cannot drift.
 */
export const trailDifficultyTypeSchema = z.enum([
  "unknown",
  "hiking",
  "mountain-hiking",
  "demanding-mountain-hiking",
  "alpine-hiking",
  "demanding-alpine-hiking",
  "difficult-alpine-hiking",
  "mtb-0",
  "mtb-1",
  "mtb-2",
  "mtb-3",
  "mtb-4",
  "mtb-5",
  "mtb-6",
]) satisfies z.ZodType<TrailDifficultyType>;

/**
 * indexedInfoSchema builds the coordinate-index-ranged analysis-entry schema for
 * a given value type: the inclusive [from_index, to_index] window into the
 * route's coordinates and the classified value. It mirrors the IndexedInfo shape
 * the elevation panel reads, parameterized by the per-kind value schema so one
 * builder serves the surface/way-type (enum) and slope (number) arrays.
 */
const indexedInfoSchema = <T extends z.ZodTypeAny>(value: T) =>
  z.object({
    from_index: z.number().int(),
    to_index: z.number().int(),
    value,
  });

/**
 * liveSegmentRouteAnalysisSchema validates the surface/way-type/slope/
 * trail-difficulty analysis a routed result carries once it is converted to the
 * snake_case indices + REST vocabulary the persisted segment uses. It is the
 * wire→session boundary validator the import + suggest drafts share, pinned to
 * LiveSegmentRouteAnalysis via `satisfies` so the validated shape and the
 * projected shape cannot drift.
 */
export const liveSegmentRouteAnalysisSchema = z.object({
  slopeInfo: z.array(indexedInfoSchema(z.number())),
  surfaceInfo: z.array(indexedInfoSchema(surfaceTypeSchema)),
  trailDifficultyInfo: z.array(indexedInfoSchema(trailDifficultyTypeSchema)),
  waytypeInfo: z.array(indexedInfoSchema(waytypeTypeSchema)),
}) satisfies z.ZodType<LiveSegmentRouteAnalysis>;

/**
 * liveSegmentRouteElevationSchema validates the per-route elevation summary a
 * routed result carries: the altitude range, total gain/loss, and the steepest
 * ascending/descending gradients. Pinned to LiveSegmentRouteElevation via
 * `satisfies` so the validated shape matches the projected one.
 */
export const liveSegmentRouteElevationSchema = z.object({
  altitudeMax: z.number(),
  altitudeMin: z.number(),
  ascent: z.number(),
  descent: z.number(),
  gradientMax: z.number(),
  gradientMin: z.number(),
}) satisfies z.ZodType<LiveSegmentRouteElevation>;

/**
 * LiveSegmentRoute is the platform-neutral shape buildLiveSegment projects onto
 * a segment: the 3D routed line (lng/lat/elevation), the route distance and
 * duration (meters/seconds), and — when the router computed them — the
 * elevation summary and the surface/way-type/slope analysis. It is the
 * core-leaf mirror of the map package's SegmentRoute (core cannot depend on
 * map); the directions engine emits a SegmentRoute, the hook hands it through
 * here as a LiveSegmentRoute to project onto the panel's ItinerarySegment.
 */
export interface LiveSegmentRoute {
  analysis?: LiveSegmentRouteAnalysis;
  distance: number;
  duration: number;
  elevation?: LiveSegmentRouteElevation;
  geometry: LineString;
}

/**
 * GraphqlRouteShape is the slice of a Data-API routed result the live-route
 * projector reads: the routed line geometry, the distance/duration metrics, and
 * the optional elevation summary + surface/way-type/slope analysis blocks. Both
 * the directions and the suggestions queries return this shape (DirectionsRoute
 * and SuggestionRoute share it), so the projector takes the structural subset
 * rather than either named wire type — the geometry is the GeoJSON scalar a
 * routed result carries, narrowed to a LineString at the single boundary the
 * caller crosses.
 */
export interface GraphqlRouteShape {
  analysis?: GeometryAnalysis;
  distance: number;
  duration: number;
  elevation?: ElevationSummary;
  geometry: LineString;
}

/**
 * dataSurfaceTypeSchema is the Data-API surface vocabulary (SCREAMING_SNAKE) an
 * analysis band carries before the projection normalizes it — the same
 * OpenRouteService classifications as surfaceTypeSchema, in the wire form the
 * GraphQL enum marshals. Pinned to the wire DataSurfaceType via `satisfies` so the
 * accepted set cannot drift from the Data-API enum the converter maps from.
 */
export const dataSurfaceTypeSchema = z.enum([
  "ASPHALT",
  "COBBLESTONE",
  "COMPACTED_GRAVEL",
  "CONCRETE",
  "DIRT",
  "FINE_GRAVEL",
  "GRASS",
  "GRASS_PAVER",
  "GRAVEL",
  "GROUND",
  "ICE",
  "METAL",
  "PAVED",
  "PAVING_STONES",
  "SAND",
  "UNKNOWN",
  "UNPAVED",
  "WOOD",
  "WOODCHIPS",
]) satisfies z.ZodType<DataSurfaceType>;

/**
 * dataWaytypeTypeSchema is the Data-API way-type vocabulary (SCREAMING_SNAKE) an
 * analysis band carries before the projection normalizes it. Pinned to the wire
 * DataWaytypeType via `satisfies` so the two cannot drift.
 */
export const dataWaytypeTypeSchema = z.enum([
  "CONSTRUCTION",
  "CYCLEWAY",
  "FERRY",
  "FOOTWAY",
  "PATH",
  "ROAD",
  "STATE_ROAD",
  "STEPS",
  "STREET",
  "TRACK",
  "UNKNOWN",
]) satisfies z.ZodType<DataWaytypeType>;

/**
 * dataTrailDifficultyTypeSchema is the Data-API trail-difficulty vocabulary
 * (SCREAMING_SNAKE) an analysis band carries before the projection normalizes
 * it. Pinned to the wire DataTrailDifficultyType via `satisfies` so the two
 * cannot drift.
 */
export const dataTrailDifficultyTypeSchema = z.enum([
  "ALPINE_HIKING",
  "DEMANDING_ALPINE_HIKING",
  "DEMANDING_MOUNTAIN_HIKING",
  "DIFFICULT_ALPINE_HIKING",
  "HIKING",
  "MOUNTAIN_HIKING",
  "MTB_0",
  "MTB_1",
  "MTB_2",
  "MTB_3",
  "MTB_4",
  "MTB_5",
  "MTB_6",
  "UNKNOWN",
]) satisfies z.ZodType<DataTrailDifficultyType>;

/**
 * DATA_TO_REST_SURFACE maps each Data-API surface value to the REST surface_info
 * value the persisted segment carries (SCREAMING_SNAKE → lower-case-hyphen). It is
 * an exhaustive Record keyed on the generated DataSurfaceType and valued in the
 * generated SurfaceType, so a member added or dropped on either side fails the type
 * check; a test asserts the runtime bijection over the full generated unions.
 */
const DATA_TO_REST_SURFACE: Record<DataSurfaceType, SurfaceType> = {
  ASPHALT: "asphalt",
  COBBLESTONE: "cobblestone",
  COMPACTED_GRAVEL: "compacted-gravel",
  CONCRETE: "concrete",
  DIRT: "dirt",
  FINE_GRAVEL: "fine-gravel",
  GRASS: "grass",
  GRASS_PAVER: "grass-paver",
  GRAVEL: "gravel",
  GROUND: "ground",
  ICE: "ice",
  METAL: "metal",
  PAVED: "paved",
  PAVING_STONES: "paving-stones",
  SAND: "sand",
  UNKNOWN: "unknown",
  UNPAVED: "unpaved",
  WOOD: "wood",
  WOODCHIPS: "woodchips",
};

/**
 * DATA_TO_REST_WAYTYPE maps each Data-API way-type value to the REST waytype_info
 * value the persisted segment carries. Exhaustive Record keyed on the generated
 * DataWaytypeType and valued in the generated WaytypeType, so a drift on either side
 * fails the type check; a test asserts the runtime bijection.
 */
const DATA_TO_REST_WAYTYPE: Record<DataWaytypeType, WaytypeType> = {
  CONSTRUCTION: "construction",
  CYCLEWAY: "cycleway",
  FERRY: "ferry",
  FOOTWAY: "footway",
  PATH: "path",
  ROAD: "road",
  STATE_ROAD: "state-road",
  STEPS: "steps",
  STREET: "street",
  TRACK: "track",
  UNKNOWN: "unknown",
};

/**
 * DATA_TO_REST_TRAIL_DIFFICULTY maps each Data-API trail-difficulty value to the
 * REST trail_difficulty_info value the persisted segment carries. Both grading scales
 * live in the one map because they share one field; they overlap only on
 * UNKNOWN, so a value still names the scale it was graded on. Exhaustive Record
 * keyed on the generated DataTrailDifficultyType and valued in the generated
 * TrailDifficultyType, so a drift on either side fails the type check; a test
 * asserts the runtime bijection.
 */
const DATA_TO_REST_TRAIL_DIFFICULTY: Record<DataTrailDifficultyType, TrailDifficultyType> = {
  ALPINE_HIKING: "alpine-hiking",
  DEMANDING_ALPINE_HIKING: "demanding-alpine-hiking",
  DEMANDING_MOUNTAIN_HIKING: "demanding-mountain-hiking",
  DIFFICULT_ALPINE_HIKING: "difficult-alpine-hiking",
  HIKING: "hiking",
  MOUNTAIN_HIKING: "mountain-hiking",
  MTB_0: "mtb-0",
  MTB_1: "mtb-1",
  MTB_2: "mtb-2",
  MTB_3: "mtb-3",
  MTB_4: "mtb-4",
  MTB_5: "mtb-5",
  MTB_6: "mtb-6",
  UNKNOWN: "unknown",
};

/**
 * toRestSurface maps a Data-API surface value to the REST surface_info value the
 * persisted segment carries, so the routed analysis reads identically to the panel's
 * stored render path. The input is the generated Data-API enum, so the lookup is
 * total and needs no cast.
 */
export const toRestSurface = (value: DataSurfaceType): SurfaceType => DATA_TO_REST_SURFACE[value];

/**
 * toRestWaytype maps a Data-API way-type value to the REST waytype_info value the
 * persisted segment carries. Total over the generated enum, so no cast.
 */
export const toRestWaytype = (value: DataWaytypeType): WaytypeType => DATA_TO_REST_WAYTYPE[value];

/**
 * toRestTrailDifficulty maps a Data-API trail-difficulty value to the REST
 * trail_difficulty_info value the persisted segment carries. Total over the generated
 * enum, so no cast.
 */
export const toRestTrailDifficulty = (value: DataTrailDifficultyType): TrailDifficultyType =>
  DATA_TO_REST_TRAIL_DIFFICULTY[value];

/**
 * toAnalysisEntries maps a list of Data-API indexed-info entries into the
 * coordinate-index-ranged shape the segment analysis arrays carry: snake_case
 * indices and a (string-)enum value normalized to the REST vocabulary. The
 * generic `convert` callback narrows the value type per analysis kind so a slope
 * entry stays a number while surface/way-type entries land in their REST enum.
 */
const toAnalysisEntries = <
  Source extends { fromIndex: number; toIndex: number; value: unknown },
  Target,
>(
  entries: Source[],
  convert: (value: Source["value"]) => Target,
): IndexedInfo<Target>[] =>
  entries.map((entry) => ({
    from_index: entry.fromIndex,
    to_index: entry.toIndex,
    value: convert(entry.value),
  }));

/**
 * graphqlElevationToSummary projects a Data-API elevation summary onto the
 * platform-neutral LiveSegmentRouteElevation the projector overlays — flattening
 * the altitude/gradient ranges into the flat min/max fields the persisted segment
 * carries.
 */
export const graphqlElevationToSummary = (
  elevation: ElevationSummary,
): LiveSegmentRouteElevation => ({
  altitudeMax: elevation.altitude.max,
  altitudeMin: elevation.altitude.min,
  ascent: elevation.ascent,
  descent: elevation.descent,
  gradientMax: elevation.gradient.max,
  gradientMin: elevation.gradient.min,
});

/**
 * graphqlAnalysisToEntries projects a Data-API analysis block onto the
 * LiveSegmentRouteAnalysis the projector overlays: it renames each entry's
 * indices to the snake_case the persisted arrays use and normalizes the
 * surface/way-type/trail-difficulty values to the REST vocabulary, leaving the
 * slope angle a raw number. It is the single converter both the
 * directions/suggestions route path and the freehand-line analysis path reuse,
 * so the routed analysis always reads identically to the panel's stored render
 * path.
 */
export const graphqlAnalysisToEntries = (analysis: GeometryAnalysis): LiveSegmentRouteAnalysis => ({
  slopeInfo: toAnalysisEntries<SlopeInfo, number>(analysis.slopeInfo, (value) => value),
  surfaceInfo: toAnalysisEntries<SurfaceInfo, SurfaceType>(analysis.surfaceInfo, toRestSurface),
  trailDifficultyInfo: toAnalysisEntries<TrailDifficultyInfo, TrailDifficultyType>(
    analysis.trailDifficultyInfo,
    toRestTrailDifficulty,
  ),
  waytypeInfo: toAnalysisEntries<WaytypeInfo, WaytypeType>(analysis.waytypeInfo, toRestWaytype),
});

/**
 * graphqlRouteToLiveRoute projects a Data-API routed result (a directions or a
 * suggestion route) into the platform-neutral LiveSegmentRoute buildLiveSegment
 * overlays: it carries the routed line + metrics, and — when the router computed
 * them — the elevation summary and the surface/way-type/slope/trail-difficulty
 * analysis, normalized to the snake_case indices + REST-vocabulary values the persisted
 * segment carries so the projection lands without a second translation. The
 * elevation/analysis blocks stay optional: a 2D-only response leaves them
 * undefined, which buildLiveSegment renders as the base segment's own values. It
 * is the shared wire→live-route conversion the segment editor's directions
 * routing and the suggest start-from both reuse, so the two cannot drift.
 */
export const graphqlRouteToLiveRoute = (route: GraphqlRouteShape): LiveSegmentRoute => ({
  analysis: route.analysis ? graphqlAnalysisToEntries(route.analysis) : undefined,
  distance: route.distance,
  duration: route.duration,
  elevation: route.elevation ? graphqlElevationToSummary(route.elevation) : undefined,
  geometry: route.geometry,
});

/**
 * overlayRouteAnalysis overlays a route's elevation summary + surface/way-type/
 * slope/trail-difficulty analysis onto a base segment, the field-by-field projection
 * buildLiveSegment and the suggestion-candidate view share. A missing elevation
 * block keeps the base values (the persisted summary survives a 2D-only routed
 * response); a missing analysis block keeps the base analysis bands. It does NOT
 * touch the geometry or the distance/duration metrics — those vary by caller (a
 * live edit overlays the routed line, the candidate view keeps the candidate
 * geometry), so each caller spreads the result and sets them itself.
 */
export const overlayRouteAnalysis = (
  base: ItinerarySegment,
  route: { analysis?: LiveSegmentRouteAnalysis; elevation?: LiveSegmentRouteElevation },
): ItinerarySegment => ({
  ...base,
  altitude_max: route.elevation?.altitudeMax ?? base.altitude_max,
  altitude_min: route.elevation?.altitudeMin ?? base.altitude_min,
  ascent: route.elevation?.ascent ?? base.ascent,
  descent: route.elevation?.descent ?? base.descent,
  gradient_max: route.elevation?.gradientMax ?? base.gradient_max,
  gradient_min: route.elevation?.gradientMin ?? base.gradient_min,
  slope_info: route.analysis?.slopeInfo ?? base.slope_info,
  surface_info: route.analysis?.surfaceInfo ?? base.surface_info,
  trail_difficulty_info: route.analysis?.trailDifficultyInfo ?? base.trail_difficulty_info,
  waytype_info: route.analysis?.waytypeInfo ?? base.waytype_info,
});

/**
 * buildLiveSegment projects a freshly-routed response onto the
 * ItinerarySegment shape the elevation panel renders. It keeps the segment's
 * identity (id, name, color, mode) and overlays the routed metrics
 * (distance, ascent/descent, altitude extremes, gradient extremes), the
 * analysis bands (surface/way-type/slope), and the route geometry — wrapped in
 * the single-level GeometryCollection the panel's geometry helpers expect. A
 * missing elevation block keeps the base values (the persisted summary survives
 * a 2D-only routed response); a missing analysis block keeps the base analysis
 * bands. The result is a live preview, not a persist source — the
 * authoritative commit-time analysis still rides the whole-itinerary refetch on
 * commit, so this projection only lives on `editSession.liveSegment` while the
 * edit is open.
 */
export const buildLiveSegment = (
  base: ItinerarySegment,
  route: LiveSegmentRoute,
): ItinerarySegment => {
  const geojson: GeometryCollection = {
    geometries: [route.geometry],
    type: "GeometryCollection",
  };

  // Overlay the analysis + elevation through the shared field projection, then
  // set the geometry + the routed distance/duration on top — the live edit
  // replaces the segment's line with the freshly-routed one.
  return {
    ...overlayRouteAnalysis(base, route),
    distance: route.distance,
    duration: route.duration,
    geojson,
  };
};

/**
 * substituteLiveSegment overlays the in-flight live segment onto a persisted
 * segment chain so every consumer (the segment list rows, the header totals, the
 * elevation chart) reads the same live-edit metrics from one shape. It returns
 * `segments` unchanged when `live` is null (no edit open). Otherwise it replaces
 * the entry whose id matches `live` — an edit of an existing segment, whose
 * distance/ascent then track the latest routed response — or places `live` among
 * the persisted entries when no entry matches, which is the create case where no
 * persisted entry exists yet. The result is a live preview, not a persist source:
 * the authoritative metrics still ride the whole-itinerary refetch on commit, so
 * the substitution only lives while the edit session carries a liveSegment.
 *
 * `insertAnchor` is where an unmatched live segment lands: the id of the segment
 * it will follow once committed, or null for a head insert. It is what makes a
 * segment being INSERTED render in the gap it is destined for rather than at the
 * tail — the draft carries a client-minted id that matches nothing in the chain,
 * so without the anchor the only honest answer is "somewhere", and the row would
 * jump position on save. Omit it (undefined) for an append, which is the tail by
 * definition; an anchor naming a segment that is not in the chain falls back to
 * the tail rather than guessing.
 */
export const substituteLiveSegment = (
  segments: ItinerarySegment[],
  live: ItinerarySegment | null,
  insertAnchor?: string | null,
): ItinerarySegment[] => {
  if (!live) {
    return segments;
  }

  if (segments.some((segment) => segment.id === live.id)) {
    return segments.map((segment) => (segment.id === live.id ? live : segment));
  }

  if (insertAnchor === undefined) {
    return [...segments, live];
  }

  // A null anchor is the head insert: nothing precedes the new segment, so it
  // opens the chain.
  const anchorIndex = insertAnchor === null ? -1 : segments.findIndex((s) => s.id === insertAnchor);
  if (insertAnchor !== null && anchorIndex === -1) {
    return [...segments, live];
  }

  return [...segments.slice(0, anchorIndex + 1), live, ...segments.slice(anchorIndex + 1)];
};
